import CollectionName from '../utils/collections';
import BaseDocument from './BaseDocument';

/**
 * Which thread replies the coach has read in one channel, kept per root message.
 *
 * Values are Firestore `createdAt` millis — never a device clock, so a skewed machine can neither
 * hide a reply nor announce one twice.
 *
 * The document id is deterministic, so this is a direct document read and never a query. Living in
 * Firestore rather than `localStorage` is what lets the same coach see one queue on web and mobile.
 *
 * @class ChatThreadSeen
 * @extends BaseDocument
 */
class ChatThreadSeen extends BaseDocument {
  /**
   * @param {String} userId Coach the read marks belong to
   * @param {String} channelId Channel the read marks cover
   * @param {Object} [opts] Firestorter document options
   */
  constructor(userId, channelId, opts) {
    super(`${CollectionName.CHAT_THREAD_SEEN}/${ChatThreadSeen.documentId(userId, channelId)}`, opts);
    this.userId = userId;
    this.channelId = channelId;
  }

  /**
   * @param {String} userId Coach id
   * @param {String} channelId Channel id
   * @returns {String} Deterministic document id for the pair
   */
  static documentId(userId, channelId) {
    return `${userId}_${channelId}`;
  }

  /**
   * Read marks for this coach and channel, keyed by root message id. Empty until the document
   * loads, which reads as "nothing seen yet" — the same shape a coach who has never opened this
   * channel genuinely has.
   * @returns {Object} Map of root message id to millis
   */
  get seenByRoot() {
    return this.data?.seenByRoot || {};
  }

  /**
   * The channel-wide read watermark: every reply older than this counts as read regardless of
   * `seenByRoot`. `undefined` means the coach has no watermark yet — which, together with an empty
   * map, is how "never opened this channel" is recognised.
   * @returns {Number|undefined} Millis, or undefined when unset
   */
  get seenBefore() {
    return this.data?.seenBefore;
  }

  /**
   * Moves the watermark forward. Never backwards — the rules reject that, and a lowered watermark
   * would resurface every thread read in the gap.
   *
   * Written on exactly two acts: first sight of a channel, and the coach pressing Mark all read.
   * Never inferred from an empty screen: an in-flight or failed query also shows zero unread.
   * @param {Number} seenBefore Millis of the newest reply now counted as read
   * @returns {Promise<void>} Resolves once the write is acknowledged
   */
  markSeenBefore = async (seenBefore) => {
    if (!seenBefore || seenBefore <= (this.seenBefore ?? 0)) {
      return;
    }
    await this.ref.set({
      userId: this.userId,
      channelId: this.channelId,
      seenBefore,
    }, { merge: true });
  };

  /**
   * Records read marks for one or more roots in a single write.
   *
   * A `set` with `merge` rather than a whole-map write, so roots absent from `marks` keep their
   * existing values: a mark written on mobile for one root is not clobbered by a mark written here
   * for another. On the *same* root it is last-write-wins, and the guard below reads the local
   * snapshot — a client whose listener is behind can still move that root's mark backwards and the
   * thread reappears unread. Firestore rules cannot iterate map keys and there is no
   * `FieldValue.max`, so this is the guarantee available.
   *
   * Creates the document on first use, which is why `userId` and `channelId` ride along.
   *
   * Takes a map rather than a single root because seeding a channel marks every thread in the
   * loaded window at once — one round-trip, not one per thread.
   * @param {Object} marks Map of root message id to millis of the newest reply read there
   * @returns {Promise<void>} Resolves once the write is acknowledged
   */
  markThreadsRead = async (marks) => {
    const advanced = Object.entries(marks).filter(
      ([rootId, seenAt]) => rootId && seenAt && (this.seenByRoot[rootId] ?? 0) < seenAt,
    );
    if (advanced.length === 0) {
      return;
    }
    await this.ref.set({
      userId: this.userId,
      channelId: this.channelId,
      seenByRoot: Object.fromEntries(advanced),
    }, { merge: true });
  };

  /**
   * Records that the coach has read `rootId` up to `newestReplyAt`.
   * @param {String} rootId Root message id
   * @param {Number} newestReplyAt Millis of the newest reply the coach has seen on that root
   * @returns {Promise<void>} Resolves once the write is acknowledged
   */
  markThreadRead = async (rootId, newestReplyAt) => this.markThreadsRead({ [rootId]: newestReplyAt });
}

export default ChatThreadSeen;
